import { Annotation, LabelStateFormValue, LabelStateV1 } from './annotation.types';

export class ModalLabelState {
  static deepClone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }

  static parseS3Url(imagePath: string): { bucket?: string; key?: string } {
    if (!imagePath || !imagePath.includes('://')) {
      return {};
    }
    const parts = imagePath.split('://');
    if (parts.length !== 2 || !parts[1]) {
      return {};
    }
    const [, path] = parts;
    const [bucket, ...keyParts] = path.split('/');
    return {
      bucket: bucket || undefined,
      key: keyParts.length > 0 ? keyParts.join('/') : undefined,
    };
  }

  static getFormValue(raw: any): LabelStateFormValue | undefined {
    if (raw === null) {
      return null;
    }
    if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
      return raw;
    }
    if (raw && typeof raw === 'object' && raw.type === 'select') {
      if (raw.value === undefined) {
        return null;
      }
      const v = raw.value;
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' || v === null) {
        return v;
      }
      return null;
    }
    return undefined;
  }

  static buildFormStateFromItem(ctx: any, item: any, preferModelValues: boolean = true): LabelStateV1['form'] {
    const values: Record<string, LabelStateFormValue> = {};
    const requiredKeys: string[] = [];

    for (const field of (ctx.fields[0].fieldGroup ?? [])) {
      const key = field.key as string;
      if (!key) {
        continue;
      }
      if ((field as any).templateOptions?.required) {
        requiredKeys.push(key);
      }
      const modelValue = (ctx.model as any)?.[key];
      const sourceValue =
        preferModelValues && modelValue !== undefined
          ? modelValue
          : item?.[key];
      const normalized = this.getFormValue(sourceValue);
      if (normalized !== undefined) {
        values[key] = normalized;
      }
    }

    // 'Class' is a plain mat-select outside Formly — persist it separately.
    if (ctx.hasImageStrategy) {
      const classVal = preferModelValues
        ? ((ctx.model as any)?.['Class'] ?? item?.['_labelState']?.form?.values?.['Class'])
        : (item?.['_labelState']?.form?.values?.['Class'] ?? (ctx.model as any)?.['Class']);
      const normalized = this.getFormValue(classVal);
      if (normalized !== undefined) {
        values['Class'] = normalized;
      }
      requiredKeys.push('Class');
    }

    return {
      values,
      requiredKeys,
      valid: ctx.form.valid,
    };
  }

  static buildAnnotationStats(items: Annotation[]): LabelStateV1['annotations']['stats'] {
    const countByType: Record<string, number> = {};
    const countByClass: Record<string, number> = {};

    for (const ann of items) {
      countByType[ann.type] = (countByType[ann.type] ?? 0) + 1;
      if (ann.label) {
        countByClass[ann.label] = (countByClass[ann.label] ?? 0) + 1;
      }
    }

    return { countByType, countByClass };
  }

  static mergeUniqueStrings(base: string[] = [], incoming: string[] = []): string[] {
    return [...new Set([...(base ?? []), ...(incoming ?? [])])];
  }

  static ensureLabelStateForItem(
    ctx: any,
    item: any,
    source: LabelStateV1['audit']['source'] = 'manual',
    preferModelValues: boolean = true,
    labelStateKey: string,
    legacyAnnotationsKey: string
  ): LabelStateV1 {
    const existing = item?.[labelStateKey] as LabelStateV1 | undefined;
    const now = new Date().toISOString();
    const backendRecordId = item?.['id']?.['key'] ?? ctx.id ?? existing?.backendRecordId ?? '';
    const s3Url = item?.['Photo']?.['link'] ?? ctx.data?.['imagePath'] ?? '';
    const parsed = this.parseS3Url(s3Url);
    const canvas = ctx.annotationCanvasRef?.nativeElement;
    const items = this.deepClone(ctx.annotations);
    const labelingInformation = ctx.readLabelingInformation(item);
    const formState = this.buildFormStateFromItem(ctx, item, preferModelValues);
    // Merge instead of replace so older persisted keys survive partial updates.
    const mergedFormValues = {
      ...(existing?.form?.values ?? {}),
      ...formState.values,
    };
    const mergedRequiredKeys = this.mergeUniqueStrings(existing?.form?.requiredKeys, formState.requiredKeys);
    const imageClasses = this.mergeUniqueStrings(existing?.classes?.imageClasses, ctx.imageClasses);
    const annotationClasses = this.mergeUniqueStrings(existing?.classes?.annotationClasses, ctx.annotationClasses);
    const rectClasses = this.mergeUniqueStrings(existing?.classes?.rectClasses, ctx.rectClasses);
    const polygonBrushClasses = this.mergeUniqueStrings(existing?.classes?.polygonBrushClasses, ctx.polygonBrushClasses);
    const classColors = {
      ...(existing?.classes?.classColors ?? {}),
      ...ctx.classColors,
    };
    const resolvedLabelingInformation =
      labelingInformation !== undefined
        ? this.deepClone(labelingInformation)
        : existing?.labelingInformation;

    const next: LabelStateV1 = {
      version: 1,
      backendRecordId,
      labelingInformation: resolvedLabelingInformation,
      imageRef: {
        id: backendRecordId || existing?.imageRef?.id || '',
        s3Url: s3Url || existing?.imageRef?.s3Url || '',
        bucket: parsed.bucket ?? existing?.imageRef?.bucket,
        key: parsed.key ?? existing?.imageRef?.key,
        filename: s3Url ? s3Url.split('/').pop() : undefined,
        width: canvas?.width ?? existing?.imageRef?.width,
        height: canvas?.height ?? existing?.imageRef?.height,
      },
      classes: {
        imageClasses,
        annotationClasses,
        rectClasses,
        polygonBrushClasses,
        classColors,
        allowUserToEditClasses: ctx.allowUserToEditClasses,
      },
      form: {
        values: mergedFormValues,
        requiredKeys: mergedRequiredKeys,
        valid: formState.valid,
      },
      annotations: {
        items,
        stats: this.buildAnnotationStats(items),
      },
      audit: {
        createdAt: existing?.audit?.createdAt ?? now,
        updatedAt: now,
        updatedBy: existing?.audit?.updatedBy,
        source,
      },
    };

    // Keep both structures updated while legacy consumers still read _annotations.
    item[labelStateKey] = next;
    item[legacyAnnotationsKey] = this.deepClone(next.annotations.items);
    return next;
  }

  static getPersistedFormFieldValue(item: any, key: string, labelStateKey: string): LabelStateFormValue | undefined {
    const itemValue = item?.[key];
    if (itemValue !== undefined && itemValue !== null) {
      if (typeof itemValue === 'object' && itemValue.type === 'select') {
        if (itemValue.value !== undefined) {
          return itemValue.value as LabelStateFormValue;
        }
      } else if (typeof itemValue === 'string' || typeof itemValue === 'number' || typeof itemValue === 'boolean') {
        return itemValue as LabelStateFormValue;
      }
    }
    return item?.[labelStateKey]?.form?.values?.[key] as LabelStateFormValue | undefined;
  }

  static getPersistedAnnotations(item: any, labelStateKey: string, legacyAnnotationsKey: string): Annotation[] {
    const fromState = item?.[labelStateKey]?.annotations?.items;
    if (Array.isArray(fromState)) {
      return this.deepClone(fromState);
    }
    const legacy = item?.[legacyAnnotationsKey];
    if (Array.isArray(legacy)) {
      return this.deepClone(legacy);
    }
    return [];
  }

  static hydrateClassesFromLabelState(ctx: any, item: any, labelStateKey: string): void {
    const state = item?.[labelStateKey] as LabelStateV1 | undefined;
    if (!state?.classes) {
      return;
    }

    ctx.imageSessionClasses = [
      ...new Set([...(ctx.imageSessionClasses ?? []), ...(state.classes.imageClasses ?? [])]),
    ];
    if (state.classes.rectClasses) {
      ctx.rectSessionClasses = [
        ...new Set([...(ctx.rectSessionClasses ?? []), ...(state.classes.rectClasses ?? [])]),
      ];
    }
    if (state.classes.polygonBrushClasses) {
      ctx.polygonBrushSessionClasses = [
        ...new Set([...(ctx.polygonBrushSessionClasses ?? []), ...(state.classes.polygonBrushClasses ?? [])]),
      ];
    }
    if (!state.classes.rectClasses && !state.classes.polygonBrushClasses) {
      // Backward compat: distribute old combined annotationClasses to both
      const legacy = state.classes.annotationClasses ?? [];
      ctx.rectSessionClasses = [...new Set([...(ctx.rectSessionClasses ?? []), ...legacy])];
      ctx.polygonBrushSessionClasses = [...new Set([...(ctx.polygonBrushSessionClasses ?? []), ...legacy])];
    }
    ctx.classColors = {
      ...(state.classes.classColors ?? {}),
      ...ctx.classColors,
    };
  }
}
